import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { PencilSimple } from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import { format, parseISO } from 'date-fns';
import { useTypedSelector } from '../../reducers';

interface AvailableBlockPopupProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  timeSlot: string;
  date: string;
  clinicianId?: string;
  onMarkUnavailable?: (date: string, timeSlot: string) => void;
}

export default function AvailableBlockPopup({
  anchorEl,
  open,
  onClose,
  timeSlot,
  date,
  clinicianId,
  onMarkUnavailable,
}: AvailableBlockPopupProps) {
  const navigate = useNavigate();
  const { viewMode } = useTypedSelector((s) => s.auth);

  const today = format(new Date(), 'yyyy-MM-dd');
  const isPast = date <= today;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      sx={{ ml: 1 }}
    >
      <Box
        sx={{
          p: 2,
          width: 280,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          borderTop: '4px solid',
          borderTopColor: 'success.main',
        }}
      >
        <Stack spacing={1}>
          <Box>
            <Typography
              variant="h6"
              sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
            >
              Marked as available in your schedule
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
            >
              Users can book appointments with you in this time
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mt: 1, wordWrap: 'break-word', whiteSpace: 'normal' }}
            >
              {date ? format(parseISO(date), 'EEEE d MMMM yyyy') : ''}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
            >
              {timeSlot}
            </Typography>
          </Box>
          <Divider />
          {!isPast && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onMarkUnavailable?.(date, timeSlot);
                onClose();
              }}
            >
              Mark block as unavailable
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PencilSimple size={16} />}
            size="small"
            fullWidth
            onClick={() => {
              const route =
                viewMode === 'clinician'
                  ? '/appointments/edit-schedule'
                  : `/clinicians/${clinicianId}/edit-schedule`;
              navigate(route);
            }}
          >
            Edit your schedule
          </Button>
        </Stack>
      </Box>
    </Popover>
  );
}
